import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { alpha, CSSObject, styled } from '@mui/material/styles';

import { doNotForwardTheseProps } from '@/styles/utils';

import { AccountTypeStatus } from '@/types/api/account';

export const AccountTypeCardContainer = styled(Paper, {
  shouldForwardProp: doNotForwardTheseProps(
    'isActive',
    'isSelected',
    'status',
    'isDisabled',
  ),
})<{
  isActive?: boolean;
  isSelected?: boolean;
  status?: AccountTypeStatus;
  isDisabled?: boolean;
}>(({ theme, isSelected, status, isDisabled }) => {
  const getInteractiveStyles = (): CSSObject => {
    if (isDisabled) {
      return {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        cursor: 'not-allowed',
      };
    }

    if (status === AccountTypeStatus.Active) {
      if (isSelected) {
        return {
          backgroundColor: theme.palette.primaries[700],
          color: theme.palette.background.paper,
          border: `1px solid ${theme.palette.primaries[700]}`,
          cursor: 'pointer',
          '.AccountTypeCardCheckBox': {
            backgroundColor: theme.palette.background.paper,
            boxShadow: `none`,
          },
          '.AccountTypeActionButton': {
            borderColor: theme.palette.background.paper,
            color: theme.palette.background.paper,
          },

          ':hover': {
            backgroundColor: theme.palette.primaries[700],
            borderColor: theme.palette.primaries[700],
          },
        };
      } else {
        return {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.grey[200]}`,
          cursor: 'pointer',

          ':hover': {
            backgroundColor: theme.palette.primaries[100],
            borderColor: theme.palette.primaries[200],
            '.AccountTypeCardCheckBox': {
              backgroundColor: theme.palette.primaries[200],
              boxShadow: `inset 0 0 0 2px ${theme.palette.primaries[400]}`,
            },
          },
        };
      }
    } else if (status === AccountTypeStatus.ComingSoon) {
      return {
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.text.primary,
        ':hover': {
          backgroundColor: theme.palette.primaries[100],
          borderColor: theme.palette.primaries[200],
        },
      };
    }

    return {};
  };

  return {
    paddingTop: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    position: 'relative',
    minHeight: 310,
    width: 264,
    height: 'auto',
    transition: theme.transitions.create([
      'height',
      'background-color',
      'color',
      'border-color',
      'box-shadow',
    ]),

    '.AccountTypeName': {
      flex: 1,
    },

    ul: {
      margin: 0,
      padding: 0,
      paddingLeft: theme.spacing(2.5),

      li: {
        listStyle: 'initial',
      },
    },

    ...getInteractiveStyles(),

    [theme.breakpoints.up('sm')]: {
      minHeight: 410,
      display: 'flex',
      flexDirection: 'column',
      width: 320,
    },
    [theme.breakpoints.up('lg')]: {
      width: 344,
      minHeight: 385,
    },
  };
});

export const AccountTypeCardContent = styled(Box, {
  shouldForwardProp: doNotForwardTheseProps('isDisabled'),
})<{ isDisabled?: boolean }>(({ isDisabled }) => ({
  opacity: isDisabled ? 0.22 : 1,
}));

export const AccountTypeCardCheckBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 6,
  boxShadow: `inset 0 0 0 2px ${theme.palette.grey[500]}`,
  height: 24,
  width: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: theme.transitions.create(['background-color', 'box-shadow']),
  transform: `translateY(${theme.spacing(1)})`,
}));

export const AccountTypeCardSelectedIcon = styled(Box)(({ theme }) => ({
  borderRadius: '50%',
  backgroundColor: theme.palette.text.primary,
  padding: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
}));

export const HighlightContainer = styled(Box)(({ theme }) => ({
  wordBreak: 'break-word',
  marginBottom: theme.spacing(3),
}));

export const AccountTypeActionContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-end',
});

export const AdditionalInfoContainer = styled(Box)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(3),
}));

export const AccountTypeActionButton = styled(Button, {
  shouldForwardProp: doNotForwardTheseProps('isHidden', 'textColor'),
})<{
  isHidden?: boolean;
  textColor?: string;
}>(({ theme, isHidden, textColor }) => ({
  borderColor: textColor || theme.palette.text.primary,
  backgroundColor: 'transparent',
  color: textColor || theme.palette.text.primary,
  transition: theme.transitions.create([
    'background-color',
    'opacity',
    'height',
    'border-color',
    'color',
  ]),

  ':hover': {
    backgroundColor: alpha(String(theme.palette.primaries[300]), 0.2),
    borderColor: textColor || theme.palette.text.primary,
    color: textColor || theme.palette.text.primary,
  },

  ...(isHidden
    ? {
        opacity: 0,
        height: 0,
        visibility: 'hidden',
      }
    : {
        opacity: 1,
        visibility: 'visible',
      }),
}));

export const AccountTypeProceedButton = styled(Button, {
  shouldForwardProp: doNotForwardTheseProps('isCaymanTenant'),
})<{
  isCaymanTenant?: boolean;
}>(({ theme, isCaymanTenant }) => ({
  ...(isCaymanTenant
    ? {
        backgroundColor: theme.palette.primaries[500],
        color: theme.palette.common.white,
      }
    : {}),
}));
